import { XIcon } from "@heroicons/react/outline";
import { ArrowLeftRightIcon, DownloadIcon, EyeIcon, FileTextIcon } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { sizeConfig } from "@/components/ui/MediaBlocks/constants";
import { useMediaFullscreen } from "@/components/ui/MediaBlocks/media-fullscreen-context";
import { PdfBlockProps } from "@/components/ui/MediaBlocks/types";
import { downloadMedia } from "@/components/ui/MediaBlocks/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

export const PdfBlock = ({
  media,
  onDelete,
  onReplace,
  readonly = true,
  size = "md",
}: PdfBlockProps) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { openFullscreen } = useMediaFullscreen();
  const isXs = size === "xs";

  const tooltipContent = (
    <div className="flex flex-col gap-0.5">
      <span className="font-medium">{media.title || "PDF Document"}</span>
      <span className="truncate text-gray-300">
        {media.url.startsWith("data:") ? "Embedded PDF" : media.url}
      </span>
    </div>
  );

  const headerIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
      <FileTextIcon className="h-5 w-5 text-red-600" />
    </div>
  );

  const handleOpenFullscreen = () => {
    openFullscreen({
      title: media.title || "PDF Document",
      downloadUrl: media.url,
      downloadFilename: media.title || "document.pdf",
      variant: "light",
      maxWidth: "5xl",
      headerIcon,
      headerSubtitle: media.url.startsWith("data:") ? "Embedded PDF" : media.url,
      content: (
        <div className="h-[calc(90vh-80px)] w-full">
          <iframe
            src={media.url}
            title={media.title || "PDF Document"}
            className="h-full w-full border-none"
          />
        </div>
      ),
    });
  };

  if (isXs) {
    return (
      <BasicTooltip content={tooltipContent} side="top">
        <div
          className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-gray-100 transition-opacity hover:opacity-80"
          onClick={handleOpenFullscreen}
        >
          <FileTextIcon className="h-8 w-8 text-gray-500" />
        </div>
      </BasicTooltip>
    );
  }

  return (
    <div
      className={cn(
        "group/media flex items-center justify-between rounded-lg border border-gray-200 bg-white",
        "flex-shrink-0",
        sizeConfig.padding[size],
      )}
    >
      <div
        className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
        onClick={handleOpenFullscreen}
      >
        <div
          className={cn(
            "flex items-center justify-center rounded-lg bg-gray-100",
            sizeConfig.icon[size],
          )}
        >
          <FileTextIcon className={cn("text-gray-500", sizeConfig.iconInner[size])} />
        </div>

        <div className="flex min-w-0 flex-col">
          <span className="max-w-full truncate font-medium text-gray-900">
            {media.title || "PDF Document"}
          </span>
          <span className="truncate text-sm text-gray-500" style={{ maxWidth: "200px" }}>
            {media.url.startsWith("data:") ? "Embedded PDF" : media.url}
          </span>
        </div>
      </div>
      <div
        className={cn(
          "flex flex-shrink-0 items-center gap-1 overflow-hidden",
          "ease-[cubic-bezier(0.175,0.885,0.32,1.275)] transition-all duration-300",
          "scale-0 group-hover/media:scale-100",
          "max-w-0 group-hover/media:max-w-40",
          "opacity-0 group-hover/media:opacity-100",
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleOpenFullscreen}
          aria-label="View PDF"
          className="h-8 w-8 text-gray-400 hover:text-gray-600"
        >
          <EyeIcon className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => downloadMedia(media.url, media.title || "document.pdf")}
          aria-label="Download PDF"
          className="h-8 w-8 text-gray-400 hover:text-gray-600"
        >
          <DownloadIcon className="h-4 w-4" />
        </Button>
        {!readonly && onReplace && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Replace PDF"
              className="h-8 w-8 text-gray-400 hover:text-gray-600"
            >
              <ArrowLeftRightIcon className="h-4 w-4" />
            </Button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="application/pdf"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  onReplace(file);
                }
              }}
            />
          </>
        )}
        {!readonly && onDelete && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onDelete}
            aria-label="Delete PDF"
            className="h-8 w-8 text-gray-400 hover:text-gray-600"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
